// src/components/ui/GuideViewer.js
// Lightbox viewer for static screenshot guides.
// Built on the Modal primitive (D-05) — no custom dialog shell or manual portal.
// Displays one screenshot + caption per step with a next/prev stepper and a
// "current / total" counter. A "Launch interactive tour" button closes the
// lightbox and starts the matching driver.js tour (D-07).
//
// Mount contract: NO isOpen prop. Caller renders {open && <GuideViewer …>}.
//
// JIT TRAP: maxWidth and image maxHeight are applied via inline style — NEVER
// as template-literal Tailwind classes. See Modal.js lines 12–16 for rationale.

import React from 'react';
import Modal from './primitives/Modal';
import Button from './primitives/Button';
import { useTour } from '../features/tours/TourContext';
import catalog from '../../guideCatalog.json';
import { getHelpManifest, findArticleForTour } from '../../services/helpContentClient';

// ---- Icons (inline SVG — no external icon library) ----

function ChevronLeftIcon() {
    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
        >
            <polyline points="15 18 9 12 15 6" />
        </svg>
    );
}

function ChevronRightIcon() {
    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
        >
            <polyline points="9 18 15 12 9 6" />
        </svg>
    );
}

// ---- GuideViewer ----

/**
 * Full-screen lightbox stepper for static screenshot guides.
 *
 * @param {{ tourId: string, onClose: () => void, restoreFocusRef?: React.RefObject }} props
 * restoreFocusRef (47-08, ACCESS-06): optional — the caller (HelpMenuPanel's
 * handleReadGuide) closes its own drawer and opens this viewer in the same
 * batched handler, which unmounts the "Read the guide" trigger in the same
 * commit this viewer mounts in. By the time Modal's own focus effect would
 * read document.activeElement, the browser has already moved it to <body>
 * because the trigger left the document. The caller captures the trigger
 * into a ref BEFORE closing the drawer, so this stays the right element.
 */
// D-09: the dialog must never exceed the viewport. `min(860, viewportWidth -
// 32)` leaves 16px of margin per side on a phone while staying at the
// designed 860 everywhere it fits. Guarded for a non-browser render (SSR/test
// evaluation without a `window`), which falls back to the static 860.
function computeMaxWidth() {
    if (typeof window === 'undefined') return 860;
    return Math.min(860, window.innerWidth - 32);
}

export default function GuideViewer({ tourId, onClose, restoreFocusRef }) {
    const { launchTour } = useTour();
    const [stepIndex, setStepIndex] = React.useState(0);

    // Lazy initializer reads window.innerWidth once at mount; the resize
    // listener below keeps it live across viewport changes (e.g. device
    // rotation) without a second render on every mount.
    const [maxWidth, setMaxWidth] = React.useState(computeMaxWidth);
    React.useEffect(() => {
        const handleResize = () => setMaxWidth(computeMaxWidth());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Reset to the first step whenever the tour changes so a reused viewer never
    // indexes past a shorter tour's step list (WR-01).
    React.useEffect(() => { setStepIndex(0); }, [tourId]);

    // Look up the tour entry by flat-searching catalog.groups.
    const tourEntry = React.useMemo(() => {
        for (const group of catalog.groups) {
            const found = group.tours.find((t) => t.tourId === tourId);
            if (found) return found;
        }
        return null;
    }, [tourId]);

    // D-08: the footer's tour -> article link — footer only (not the
    // drawer's Guides rows, which Phase 42 already made dense, and not the
    // driver.js popovers, which would mean touching 19 step builders). D-06:
    // derived at runtime from the shared manifest reverse lookup, never a
    // second authored tour-to-article list. Reset to null at the top of the
    // effect so a tourId change cannot briefly show the previous tour's
    // article link, and swallow a manifest-fetch rejection — a failed fetch
    // must not break the guide the user is currently reading.
    const [relatedArticle, setRelatedArticle] = React.useState(null);
    React.useEffect(() => {
        setRelatedArticle(null);
        let cancelled = false;
        (async () => {
            const manifest = await getHelpManifest();
            const found = findArticleForTour(tourId, manifest);
            if (!cancelled) setRelatedArticle(found);
        })().catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [tourId]);

    // Close the lightbox before starting the driver.js tour — no double overlay (D-07).
    const handleLaunchTour = () => {
        onClose();
        launchTour(tourId);
    };

    // Keyboard Left/Right for step navigation — handled on the body div.
    const handleKeyDown = (e) => {
        if (!tourEntry) return;
        if (e.key === 'ArrowRight' && stepIndex < tourEntry.steps.length - 1) {
            setStepIndex((i) => i + 1);
        } else if (e.key === 'ArrowLeft' && stepIndex > 0) {
            setStepIndex((i) => i - 1);
        }
    };

    // ---- Fallback for unknown tourId ----
    if (!tourEntry) {
        return (
            <Modal
                title="Guide"
                onClose={onClose}
                maxWidth={maxWidth}
                restoreFocusRef={restoreFocusRef}
            >
                <p
                    style={{
                        fontSize: 14,
                        lineHeight: 1.5,
                        color: 'var(--text-muted)',
                        textAlign: 'center',
                        padding: '32px 0',
                    }}
                >
                    Guide not available yet. Try reopening the Help menu, or launch the interactive tour instead.
                </p>
            </Modal>
        );
    }

    const steps = tourEntry.steps;
    const total = steps.length;
    const step = steps[stepIndex];
    const isFirst = stepIndex === 0;
    const isLast = stepIndex === total - 1;

    // ---- Footer: prev / counter / next / launch ----
    // D-09: five controls in one row overflow a 360px viewport. flexWrap lets
    // trailing controls drop to a further line instead of clipping or forcing
    // the dialog to scroll sideways; justifyContent centers each wrapped row
    // the same way the single row was centered before. One `gap: 8` covers
    // both axes (the Spacing Scale's `sm` row) rather than separate
    // column/row gaps.
    const footer = (
        <div
            style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                width: '100%',
            }}
        >
            {/* Previous — hidden on step 1 (removed from DOM, not just disabled) */}
            {!isFirst && (
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setStepIndex((i) => i - 1)}
                    aria-label="Previous step"
                >
                    <ChevronLeftIcon />
                    Previous
                </Button>
            )}

            {/* Step counter — center, aria-live for screen reader announcements.
                `flex: 1` on a wrapping flex row would make the counter claim
                an entire wrapped line by itself; `flex: '1 1 auto'` with a
                minWidth wide enough for "NN / NN" lets it size to its content
                on every line while the wrapper's justifyContent: 'center'
                keeps it centered relative to its siblings, both unwrapped at
                860px and wrapped at 360px. */}
            <span
                aria-live="polite"
                style={{
                    flex: '1 1 auto',
                    minWidth: 48,
                    textAlign: 'center',
                    fontSize: 12,
                    color: 'var(--text-muted)',
                }}
            >
                {stepIndex + 1} / {total}
            </span>

            {/* Next — hidden on last step (removed from DOM, not just disabled) */}
            {!isLast && (
                <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setStepIndex((i) => i + 1)}
                    aria-label="Next step"
                >
                    Next
                    <ChevronRightIcon />
                </Button>
            )}

            {/* Article link (D-08) — resolved from the shared reverse
                lookup (D-06), hidden entirely, never a disabled control, when
                no article claims this tour. Opens in a new tab (D-13): the
                reader is mid-tour, and a new-tab open doesn't compete for the
                same viewport, so unlike handleLaunchTour there is no need to
                close the lightbox first — it stays open behind the tab. */}
            {relatedArticle && (
                <Button
                    as="a"
                    href={`/help/${relatedArticle.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="ghost"
                    size="sm"
                >
                    Read the full article
                </Button>
            )}

            {/* Launch interactive tour — always visible (D-07) */}
            <Button
                variant="accent"
                size="sm"
                onClick={handleLaunchTour}
            >
                Launch interactive tour
            </Button>
        </div>
    );

    return (
        <Modal
            title={tourEntry.name}
            onClose={onClose}
            maxWidth={maxWidth}
            restoreFocusRef={restoreFocusRef}
            footer={footer}
        >
            {/* Body: keyboard listener for Left/Right arrow navigation */}
            <div onKeyDown={handleKeyDown}>
                {/* Screenshot area */}
                <div
                    style={{
                        background: 'var(--surface-muted)',
                        borderRadius: 8,
                        overflow: 'hidden',
                        lineHeight: 0,
                    }}
                >
                    <img
                        src={`/${step.imagePath}`}
                        loading="lazy"
                        alt={step.caption}
                        style={{
                            width: '100%',
                            maxHeight: 480,
                            objectFit: 'contain',
                            display: 'block',
                        }}
                    />
                </div>

                {/* Caption */}
                <p
                    style={{
                        fontSize: 14,
                        lineHeight: 1.5,
                        color: 'var(--text-primary)',
                        marginTop: 16,
                        marginBottom: 0,
                    }}
                >
                    {step.caption}
                </p>
            </div>
        </Modal>
    );
}
